import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import upload from "../assets/ic_upload.svg";
import config from "../config";
import { CATEGORY_OPTIONS, resolveCanonicalCategory } from "../constants/categories";
import { CONTENT_TYPE_OPTIONS } from "../constants/contentTypes";

const TITLE_OPTIONS = [
  "Root Chakra",
  "Sacral Chakra",
  "Solar Plexus Chakra",
  "Heart Chakra",
  "Throat Chakra",
  "Third Eye Chakra",
  "Crown Chakra",
];

const ACCEPT_BY_TYPE = {
  video: "video/*",
  image_text: "image/*",
  audio_text: "audio/*",
};

const UPLOAD_LABELS = {
  video: "Replace Video (optional)",
  image_text: "Replace Image (optional)",
  audio_text: "Replace Audio (optional)",
};

const EditVideo = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLocked, setIsLocked] = useState(true);
  const [newMediaFile, setNewMediaFile] = useState(null);
  const [existingMediaUrl, setExistingMediaUrl] = useState("");
  const [title, setTitle] = useState("Root Chakra");
  const [category, setCategory] = useState("Starlight Yoga");
  const [contentType, setContentType] = useState("video");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    const loadVideo = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}/videos/${id}`);
        const data = await res.json();

        if (!data.success || !data.data?.video) {
          setMessage({ text: data.message || "Content not found.", type: "error" });
          return;
        }

        const video = data.data.video;
        setTitle(video.title || TITLE_OPTIONS[0]);
        setCategory(resolveCanonicalCategory(video.category));
        setIsLocked(!!video.lock);
        setContentType(video.contentType || "video");
        setDescription(video.description || "");
        setExistingMediaUrl(`${config.BASE_URL}/uploads/${video.url}`);
      } catch (err) {
        console.error(err);
        setMessage({ text: "Failed to load content.", type: "error" });
      } finally {
        setFetching(false);
      }
    };

    loadVideo();
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) {
      setMessage({ text: "File size must not exceed 500 MB.", type: "error" });
      return;
    }

    setMessage({ text: "", type: "" });
    setNewMediaFile(file);
  };

  const previewSrc = useMemo(() => {
    if (newMediaFile) return URL.createObjectURL(newMediaFile);
    return existingMediaUrl;
  }, [newMediaFile, existingMediaUrl]);

  useEffect(() => {
    if (!newMediaFile || !previewSrc.startsWith("blob:")) return;
    return () => URL.revokeObjectURL(previewSrc);
  }, [newMediaFile, previewSrc]);

  const titleOptions = useMemo(() => {
    if (title && !TITLE_OPTIONS.includes(title)) {
      return [title, ...TITLE_OPTIONS];
    }
    return TITLE_OPTIONS;
  }, [title]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if ((contentType === "image_text" || contentType === "audio_text") && !description.trim()) {
      setMessage({ text: "Description is required for this content type.", type: "error" });
      return;
    }

    const formData = new FormData();
    if (newMediaFile) {
      formData.append("video", newMediaFile);
    }
    formData.append("title", title);
    formData.append("category", category);
    formData.append("lock", isLocked);
    formData.append("contentType", contentType);
    formData.append("description", description.trim());

    try {
      setLoading(true);
      const response = await fetch(`${config.BASE_URL}/videos/${id}`, {
        method: "PUT",
        body: formData,
      });

      const data = await response.json();
      setLoading(false);

      if (data.success) {
        setMessage({ text: "Content updated successfully!", type: "success" });
        setTimeout(() => navigate("/dashboard/video"), 1500);
      } else {
        setMessage({ text: data.message || "Update failed.", type: "error" });
      }
    } catch (error) {
      console.error("Update error:", error);
      setLoading(false);
      setMessage({ text: "Something went wrong while updating.", type: "error" });
    }
  };

  const renderPreview = () => {
    if (!previewSrc) {
      return (
        <>
          <img src={upload} alt="Upload" className="w-16 h-16 mb-4 opacity-80" />
          <p className="text-gray-300 font-medium">{UPLOAD_LABELS[contentType]}</p>
        </>
      );
    }

    if (contentType === "video") {
      return (
        <video
          key={previewSrc}
          src={previewSrc}
          controls
          className="w-full h-48 rounded-lg object-cover"
        />
      );
    }

    if (contentType === "image_text") {
      return (
        <img
          key={previewSrc}
          src={previewSrc}
          alt="Preview"
          className="w-full h-48 rounded-lg object-cover"
        />
      );
    }

    return <audio key={previewSrc} src={previewSrc} controls className="w-full" />;
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-[#211436] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#211436] flex flex-col items-center justify-center p-8 relative">
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-white text-lg font-medium">Updating content...</p>
        </div>
      )}

      <form
        onSubmit={handleUpdate}
        className="bg-[#2b145c] p-10 rounded-2xl shadow-xl w-full max-w-4xl flex flex-col md:flex-row gap-8"
      >
        <label
          htmlFor="mediaUpload"
          className="flex-1 flex flex-col justify-center items-center border-2 border-dashed border-gray-400 rounded-xl cursor-pointer p-8 hover:border-purple-400 transition min-h-[348px]"
        >
          {renderPreview()}
          <p className="text-gray-400 text-xs mt-2">Click to choose a new file</p>
          <input
            id="mediaUpload"
            type="file"
            accept={ACCEPT_BY_TYPE[contentType]}
            onChange={handleFileChange}
            className="hidden"
          />
        </label>

        <div className="flex-1 flex flex-col gap-6">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Content Type</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {CONTENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Title</label>
            <select
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {titleOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-sm text-gray-300 mb-2">Select Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col justify-end">
              <label className="block text-sm text-gray-300 mb-2">Lock/Unlock</label>
              <div
                onClick={() => setIsLocked(!isLocked)}
                className="flex items-center gap-3 rounded-sm bg-[#482772] cursor-pointer p-3"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setIsLocked(!isLocked)}
              >
                <span
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition ${
                    isLocked ? "bg-purple-600" : "bg-gray-500"
                  }`}
                >
                  <span
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition ${
                      isLocked ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </span>
                <span className="text-gray-300 text-sm">
                  {isLocked ? "Locked" : "Unlocked"}
                </span>
              </div>
            </div>
          </div>

          {(contentType === "image_text" || contentType === "audio_text") && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                placeholder="Enter the text content..."
                className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard/video")}
              className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-full transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-semibold rounded-full transition"
            >
              {loading ? "Saving..." : "Update Content"}
            </button>
          </div>

          {message.text && (
            <p
              className={`mt-3 text-sm ${
                message.type === "error" ? "text-red-400" : "text-green-400"
              }`}
            >
              {message.text}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditVideo;
